import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../config';

const SidebarRight = () => {
  const [trendingTopics, setTrendingTopics] = useState([]);
  const [topContributors, setTopContributors] = useState([]);

  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    if (names.length >= 2) {
      return names[0][0] + names[1][0];
    }
    return name.substring(0, 2).toUpperCase();
  };

  const formatReputation = (rep) => {
    if (rep >= 1000) return `${(rep / 1000).toFixed(1)}k`;
    return rep.toString();
  };

  useEffect(() => {
    // Fetch trending topics
    const fetchTrending = async () => {
      try {
        const response = await fetch(getApiUrl('/api/threads/meta/trending'));
        if (response.ok) {
          const data = await response.json();
          if (data.trending && data.trending.length > 0) {
            setTrendingTopics(data.trending);
          } else {
            setTrendingTopics([]);
          }
        } else {
          setTrendingTopics([]);
        }
      } catch (error) {
        console.error('Failed to fetch trending topics:', error);
        setTrendingTopics([]);
      }
    };

    // Fetch top contributors
    const fetchContributors = async () => {
      try {
        const response = await fetch(getApiUrl('/api/users/leaderboard/top?limit=5'));
        if (response.ok) {
          const data = await response.json();
          if (data.users && data.users.length > 0) {
            setTopContributors(data.users.map((user, index) => ({
              rank: index + 1,
              initials: getInitials(user.username),
              name: user.username,
              reputation: formatReputation(user.reputation),
              username: user.username
            })));
          } else {
            setTopContributors([]);
          }
        } else {
          setTopContributors([]);
        }
      } catch (error) {
        console.error('Failed to fetch top contributors:', error);
        setTopContributors([]);
      }
    };

    // Initial fetch
    fetchTrending();
    fetchContributors();

    // Periodic updates every 60 seconds
    const trendingInterval = setInterval(fetchTrending, 60000);
    const contributorsInterval = setInterval(fetchContributors, 60000);

    return () => {
      clearInterval(trendingInterval);
      clearInterval(contributorsInterval);
    };
  }, []);

  return (
    <aside className="sidebar-right" style={{ 
      maxHeight: 'calc(100vh - 80px)', 
      overflowY: 'auto',
      position: 'sticky',
      top: '80px'
    }}>
      <div className="widget">
        <div className="widget-header">
          <span className="widget-title">Trending Topics</span>
          <span className="widget-more">See all</span>
        </div>
        <div className="trending-list">
          {trendingTopics.length > 0 ? (
            trendingTopics.map((item, index) => (
              <div className="trending-item" key={index}>
                <div className="trending-topic">{item.topic}</div>
                <div className="trending-meta">{item.discussions} discussions • {item.thisWeek} this week</div>
              </div>
            ))
          ) : (
            <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>
              No trending topics yet
            </div>
          )}
        </div>
      </div>

      <div className="widget">
        <div className="widget-header">
          <span className="widget-title">Top Contributors</span>
          <span className="widget-more">See all</span>
        </div>
        <div className="contributor-list">
          {topContributors.length > 0 ? (
            topContributors.map((contributor) => (
              <div className="contributor-item" key={contributor.rank}>
                <span className="contributor-rank">{contributor.rank}</span>
                <div className="contributor-avatar">{contributor.initials}</div>
                <div className="contributor-info">
                  <div className="contributor-name">{contributor.name}</div>
                  <div className="contributor-rep">{contributor.reputation} reputation</div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>
              No contributors yet
            </div>
          )}
        </div>
      </div>

      <div className="widget">
        <div className="widget-header">
          <span className="widget-title">Integrations</span>
          <span style={{
            fontSize: '10px',
            fontWeight: '600',
            color: 'var(--accent)',
            background: 'var(--accent-bg)',
            padding: '3px 8px',
            borderRadius: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>Coming Soon</span>
        </div>
        <div className="integration-grid" style={{ opacity: 0.5, pointerEvents: 'none' }}>
          <div className="integration-item">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
            <span>GitHub</span>
          </div>
          <div className="integration-item">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.15 2.587L18.21.21a1.494 1.494 0 0 0-1.705.29l-9.46 8.63-4.12-3.128a.999.999 0 0 0-1.276.057L.327 7.261A1 1 0 0 0 .326 8.74L3.899 12 .326 15.26a1 1 0 0 0 .001 1.479L1.65 17.94a.999.999 0 0 0 1.276.057l4.12-3.128 9.46 8.63a1.492 1.492 0 0 0 1.704.29l4.942-2.377A1.5 1.5 0 0 0 24 20.06V3.939a1.5 1.5 0 0 0-.85-1.352zm-5.146 14.861L10.826 12l7.178-5.448v10.896z"/>
            </svg>
            <span>VS Code</span>
          </div>
          <div className="integration-item">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.75 1.5a.75.75 0 0 1 .75.75v.75h.75a.75.75 0 0 1 0 1.5h-.75v.75a.75.75 0 0 1-1.5 0V4.5h-.75a.75.75 0 0 1 0-1.5h.75v-.75a.75.75 0 0 1 .75-.75Zm-12 3a.75.75 0 0 1 .75.75v2.25h2.25a.75.75 0 0 1 0 1.5H7.5v2.25a.75.75 0 0 1-1.5 0V9h-2.25a.75.75 0 0 1 0-1.5H6V5.25a.75.75 0 0 1 .75-.75Zm-.75 9a.75.75 0 0 1 .75-.75h.75v-.75a.75.75 0 0 1 1.5 0v.75h.75a.75.75 0 0 1 0 1.5h-.75v.75a.75.75 0 0 1-1.5 0v-.75h-.75a.75.75 0 0 1-.75-.75Zm8.25 3a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 0 1.5h-3a.75.75 0 0 1-.75-.75Zm.75 2.25a.75.75 0 0 0 0 1.5h3a.75.75 0 0 0 0-1.5h-3Z"/>
            </svg>
            <span>Kaggle</span>
          </div>
          <div className="integration-item">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.964 0L.672 3.974l1.784 14.794L11.976 24l9.568-5.303 1.784-14.794L11.964 0zm-.056 3.59l6.748 14.898h-2.474l-1.36-3.552H8.896l-1.36 3.552H5.104L11.908 3.59zm-.024 4.47L9.792 13.27h4.088l-2.004-5.21h.008z"/>
            </svg>
            <span>Angular</span>
          </div>
          <div className="integration-item">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M14.23 12.004a2.236 2.236 0 0 1-2.235 2.236 2.236 2.236 0 0 1-2.236-2.236 2.236 2.236 0 0 1 2.235-2.236 2.236 2.236 0 0 1 2.236 2.236zm2.648-10.69c-1.346 0-3.107.96-4.888 2.622-1.78-1.653-3.542-2.602-4.887-2.602-.41 0-.783.093-1.106.278-1.375.793-1.683 3.264-.973 6.365C1.98 8.917 0 10.42 0 12.004c0 1.59 1.99 3.097 5.043 4.03-.704 3.113-.39 5.588.988 6.38.32.187.69.275 1.102.275 1.345 0 3.107-.96 4.888-2.624 1.78 1.654 3.542 2.603 4.887 2.603.41 0 .783-.09 1.106-.275 1.374-.792 1.683-3.263.973-6.365C22.02 15.096 24 13.59 24 12.004c0-1.59-1.99-3.097-5.043-4.032.704-3.11.39-5.587-.988-6.38-.318-.184-.688-.277-1.092-.278z"/>
            </svg>
            <span>React</span>
          </div>
          <div className="integration-item">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
            </svg>
            <span>Discord</span>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default SidebarRight;

